import { ProjectsFiltersType } from 'components/projectsListFilters/ProjectsListFilters'
import { IUhubErrorResponse, IUhubSuccessResponse, Project, ProjectDetailed, StatusMessage } from 'types/Project'
import { formatDate } from 'utils'

export const STATUS_CODES = {
  PLN: 'PLN',
  CUR: 'CUR',
  CPL: 'CPL',
  STP: 'STP',
  FAIL: 'FAIL',
  UPDATED: 'UPDATED',
} as const

export type StatusCode = keyof typeof STATUS_CODES

export const STATUS_MAPPER: Record<StatusCode | string, { label: string; labelColor: string; bgColor: string }> = {
  [STATUS_CODES.PLN]: { label: 'In Progress', labelColor: '#1557CA', bgColor: '#72D2FB33' },
  [STATUS_CODES.CUR]: { label: 'In Progress', labelColor: '#1557CA', bgColor: '#72D2FB33' },
  [STATUS_CODES.CPL]: { label: 'Synced', labelColor: '#282828', bgColor: '#D4D4D433' },
  [STATUS_CODES.STP]: { label: 'Failed', labelColor: '#C10000', bgColor: '#FF8A7A33' },
  [STATUS_CODES.FAIL]: { label: 'Failed', labelColor: '#C10000', bgColor: '#FF8A7A33' },
  [STATUS_CODES.UPDATED]: { label: 'Updated', labelColor: '#FFB71E', bgColor: '#FFB71E33' },
  FAILED: { label: 'Failed', labelColor: '#C10000', bgColor: '#FF8A7A33' },
  SYNCED: { label: 'Synced', labelColor: '#282828', bgColor: '#D4D4D433' },
  'In Progress': { label: 'In Progress', labelColor: '#1557CA', bgColor: '#72D2FB33' },
  'Not Started': { label: 'Not Started', labelColor: '#1557CA', bgColor: '#72D2FB33' },
  Started: { label: 'Started', labelColor: '#006B3F', bgColor: '#98D80033' },
}

export const parseStatusCode = (
  statusCode: string,
  uhubStatus?: string,
): { label: string; labelColor: string; bgColor: string } => {
  return !statusCode
    ? {
        label: 'Null',
        labelColor: 'black',
        bgColor: 'white',
      }
    : !!uhubStatus && uhubStatus !== 'Pipeline'
      ? STATUS_MAPPER['Started']
      : STATUS_MAPPER[statusCode as StatusCode]
}

export const getBriefStatus = (
  briefs: Project[],
  workfrontProjectId: string,
): { playgroundStatus: StatusCode; uHubStatus: StatusCode } => {
  const filteredBriefs = briefs?.filter(item => item.workfrontProjectId === workfrontProjectId)

  if (!filteredBriefs.length) {
    return { playgroundStatus: 'PLN', uHubStatus: 'PLN' }
  }

  const latestBrief = filteredBriefs?.reduce((prev, curr) => (curr.version > prev.version ? curr : prev))

  const uHubStatus = filteredBriefs?.some(brief => isSuccessResponse(brief.uhubResponse)) ? 'CPL' : 'FAIL'

  let playgroundStatus: StatusCode = 'PLN'
  if (latestBrief.uhubResponse === null) {
    playgroundStatus = 'UPDATED'
  } else {
    playgroundStatus = isSuccessResponse(latestBrief?.uhubResponse) ? 'CPL' : 'FAIL'
  }

  return { playgroundStatus, uHubStatus }
}

export const getLatestBrief = (briefs: Project[], workfrontProjectId: string): Project | undefined => {
  const filteredBriefs = briefs?.filter(item => item.workfrontProjectId === workfrontProjectId)
  return !!filteredBriefs.length
    ? filteredBriefs?.reduce((prev, curr) => (curr.version > prev.version ? curr : prev))
    : undefined
}

export const isSuccessResponse = (response: IUhubSuccessResponse | IUhubErrorResponse) =>
  !!(response as IUhubSuccessResponse)?.id

export const getLatestVersion = <T extends { version: number }>(projects: T[]): T => {
  return !!projects.length ? projects?.reduce((prev, curr) => (curr.version > prev.version ? curr : prev)) : ({} as T)
}

export const getLatestSyncedBrief = (versions: ProjectDetailed[], project: Project): ProjectDetailed | undefined => {
  if (!versions.length) return undefined
  return versions.find(ver => {
    const latestBriefVersion = ver.briefs?.reduce((max, brief) => {
      return brief.version > max ? brief.version : max
    }, 0)

    return latestBriefVersion === project?.maxBriefVersion
  })
}

// This is currently used to get the expectedDueDateBrief in detailed view
export const getLatestSyncedBriefFromWorkfront = (
  versions: ProjectDetailed[],
  project: Project,
): ProjectDetailed | undefined => {
  if (!versions.length) return undefined
  return versions.find(ver => ver.briefs?.find(brief => brief.version === project.syncedBriefVersion))
}

export const getLatestWorkfrontVersion = <T extends { version: number; syncedVersion: number }>(projects: T[]): T => {
  return !!projects.length ? projects?.reduce((prev, curr) => (curr.version > prev.version ? curr : prev)) : ({} as T)
}

export const filterProjectsWithErrors = (projects?: Project[]): Project[] =>
  projects
    ?.reduce((acc: Project[], brief: Project) => {
      if (!acc.some((item: Project) => item.workfrontProjectId === brief.workfrontProjectId)) {
        acc.push(brief)
      }
      return acc
    }, [])
    ?.filter(({ uhubResponse }) => !!uhubResponse?.errors) || []

const VERSION_DIFFRENCE_EXCLUDED_FIELDS = ['id', 'version', 'briefs', 'name']

// TODO: Check syncedVersion and maxVersion
export const compareUpdatedFields = (latestWorkfrontProject: ProjectDetailed, project: Project): any => {
  if (latestWorkfrontProject.version === 1) return []

  const fields = Object.keys(latestWorkfrontProject)

  const updatedFields: any = []

  fields
    .filter(field => !VERSION_DIFFRENCE_EXCLUDED_FIELDS.includes(field))
    .forEach(field => {
      // Very ugly solution because backend is not consistent with keys in the responses
      if (field === 'operatingUnit' || field === 'regionalOperatingUnit') {
        if (
          JSON.stringify(latestWorkfrontProject.operatingUnit) !== JSON.stringify(project.operatingUnit) &&
          JSON.stringify(latestWorkfrontProject?.regionalOperatingUnit) !== JSON.stringify(project?.operatingUnit)
        ) {
          updatedFields.push(field)
        }
      } else {
        //@ts-expect-error
        if (!!project[field] && !!latestWorkfrontProject[field]) {
          //@ts-expect-error
          JSON.stringify(project[field]) !== JSON.stringify(latestWorkfrontProject[field]) && updatedFields.push(field)
        }
      }
    })

  return updatedFields
}

export const generateStatusMessage = <K extends keyof Project & keyof ProjectDetailed>(
  field: K,
  latestWorkfrontVersion: ProjectDetailed,
  latestUhubVersion: Project,
): StatusMessage | undefined => {
  // TODO: very ugly solution because backend is not consistent with keys in the responses
  if (field === 'operatingUnit' || field === 'regionalOperatingUnit') {
    if (
      JSON.stringify(latestWorkfrontVersion.operatingUnit) !== JSON.stringify(latestUhubVersion.operatingUnit) &&
      JSON.stringify(latestWorkfrontVersion?.regionalOperatingUnit) !== JSON.stringify(latestUhubVersion?.operatingUnit)
    ) {
      return {
        status: 'Brief changed on Workfront.',
        details: `(Currently on uHub: ${latestUhubVersion?.[field]})`,
      }
    }
  } else if (JSON.stringify(latestWorkfrontVersion?.[field]) !== JSON.stringify(latestUhubVersion?.[field])) {
    return {
      status: 'Brief changed on Workfront.',
      details: `(Currently on uHub: ${field === 'targetInMarketDate' && !!latestUhubVersion?.[field] ? formatDate(latestUhubVersion?.[field] as string) : latestUhubVersion?.[field]})`,
    }
  }
}

export const generateFieldStatus = (
  field: keyof ProjectDetailed,
  updatedFields: string[],
  project: Omit<ProjectDetailed, 'BriefDocument' | 'IColliderUhubUser'>,
) => {
  const fieldValue = project?.[field]
  if (updatedFields.some(f => f === field)) {
    return 'warning'
  } else if (typeof fieldValue === 'string' || Array.isArray(fieldValue)) {
    return fieldValue.length > 0 ? 'success' : 'error'
  }
  return 'error'
}

export const generateProjectLeadStatus = (updatedFields: string[], project: ProjectDetailed) => {
  const fieldValue = project?.projectLead

  if (updatedFields.some(f => f === 'projectLead')) {
    return 'warning'
  }

  // Check if 'name' has a length for IColliderUhubUser
  if (fieldValue?.name && fieldValue.name.length > 0) {
    return 'success'
  }

  return 'error'
}

export const countActiveFilters = (filters?: ProjectsFiltersType): number =>
  filters
    ? Object.values(filters).reduce((count, value) => {
        return value !== null && value !== undefined && value !== '' && value.length > 0 ? count + 1 : count
      }, 0)
    : 0

/**
 * Check expected Due date on brief level
 * expectedDueDateBriefs for non-project briefs
 * @param {ProjectDetailed[]} projectDetailed
 * @param {Project} project
 * @returns {"error" | "warning" | undefined}}
 */
export const getExpectedDueDateStatus = (versions: ProjectDetailed[], project: Project) => {
  if (!versions.length) {
    return 'error'
  }

  if (
    versions?.find(d => d.briefs?.find(brief => brief.version === project.syncedBriefVersion))?.briefs?.[0]
      ?.expectedDueDateBriefs !== getLatestVersion(versions)?.briefs?.[0]?.expectedDueDateBriefs
  ) {
    return 'warning'
  }
}

export const generateExpectedDueDateStatusMessage = (versions: ProjectDetailed[], project: Project) => {
  const latestVersion = getLatestSyncedBriefFromWorkfront(versions, project)

  return {
    details: latestVersion
      ? `Currently on uHub: ${formatDate(latestVersion?.briefs?.[0]?.expectedDueDateBriefs)}`
      : 'Data not yet synced',
    status: 'Brief changed on Workfront.',
  }
}

export const attachmentsUpdated = (project: Project, versions: ProjectDetailed[]) => {
  if (!project || !versions.length) return false

  const uhubAttachments = versions
    ?.find(ver => ver.briefs?.find(brief => brief.version === project.syncedBriefVersion))
    ?.briefs?.map(brief => brief.documents?.map(doc => doc.name))
    ?.flat()
    ?.sort()

  const workfrontAttachments = versions
    ?.find(ver => ver.briefs?.find(brief => brief.version === project.maxBriefVersion))
    ?.briefs?.map(brief => brief.documents?.map(doc => doc.name))
    ?.flat()
    ?.sort()

  let isUpdated = false

  if (!!uhubAttachments?.length && !!workfrontAttachments?.length) {
    for (let i = 0; i < uhubAttachments?.length; i++) {
      if (uhubAttachments[i] !== workfrontAttachments[i]) {
        isUpdated = true
      }
    }
  }
  return isUpdated
}

export const isNonProjectBrief = (project: Project): boolean =>
  project.maxBriefVersion > 0 && project.referenceNumber === null

export const getDeletedDocuments = (project: Project, versions: ProjectDetailed[]): { name: string; id: string }[] => {
  const oldVersion = versions
    .find(ver => ver.briefs.find(b => b.version === project.lastBriefSyncVersion))
    ?.briefs?.map(brief => brief.documents?.map(doc => ({ name: doc.name, id: doc.id })))
    ?.flat()
    ?.sort((a, b) => a.name.localeCompare(b.name))

  const versionToCompare = isNonProjectBrief(project) ? project.maxBriefVersion : project.syncedVersion

  const workfrontAttachments = versions
    ?.find(ver => ver.briefs?.find(brief => brief.version === versionToCompare))
    ?.briefs?.map(brief => brief.documents?.map(doc => ({ name: doc.name, id: doc.id })))
    ?.flat()
    ?.sort((a, b) => a.name.localeCompare(b.name))

  return oldVersion?.filter(oldDoc => !workfrontAttachments?.some(newDoc => newDoc.name === oldDoc.name)) || []
}
